import React, { useState, useEffect } from 'react';
import { X, CheckCircle, RefreshCw, Sparkles, AlertCircle, ArrowRight, Lightbulb, Users, DollarSign, Target, MapPin, Rocket, Heart } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BusinessPlanReviewModal = ({ isOpen, onClose, onApproved }) => {
  const [plan, setPlan] = useState(null);
  const [reviewSections, setReviewSections] = useState(null);
  const [validation, setValidation] = useState(null);
  const [isApproved, setIsApproved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchPlanForReview();
    }
  }, [isOpen]);

  const fetchPlanForReview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/business-brain/business-builder/review`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.has_plan) {
        setPlan(data.plan);
        setReviewSections(data.review_sections);
        setValidation(data.validation);
        setIsApproved(data.is_approved);
      } else {
        toast.error('No business plan found. Create one first.');
        onClose();
      }
    } catch (error) {
      console.error('Error fetching plan:', error);
      toast.error('Failed to load business plan');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setActionLoading('approve');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/business-brain/business-builder/review/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success && data.approved) {
        toast.success('Business plan approved! Proceeding to Money Bot...');
        setIsApproved(true);
        if (onApproved) {
          onApproved(data);
        }
        setTimeout(() => onClose(), 1500);
      } else {
        toast.error(data.message || 'Plan needs revision before approval');
        setValidation(data.validation);
      }
    } catch (error) {
      console.error('Error approving plan:', error);
      toast.error('Failed to approve plan');
    } finally {
      setActionLoading(null);
    }
  };

  // Shared: safely parse a fetch response body. Never throws the
  // DOMException "The object can not be cloned" — we read body ONCE
  // as text and JSON.parse a captured string, so Cloudflare 520 HTML
  // pages degrade to a clean error message instead of a scary toast.
  const safeParse = async (response) => {
    let rawBody = '';
    try {
      rawBody = await response.text();
    } catch (_) {
      return { data: null, rawBody: '' };
    }
    if (!rawBody) return { data: null, rawBody: '' };
    try {
      return { data: JSON.parse(rawBody), rawBody };
    } catch (_) {
      return { data: null, rawBody };
    }
  };

  const backendError = (data, status, fallback) => {
    const detail = data?.detail || data?.message;
    if (typeof detail === 'string' && detail) return detail;
    if (status === 401 || status === 403) return 'Your session expired. Please refresh and sign in again.';
    if (status === 429) return 'Too many requests right now. Please wait a moment and try again.';
    if (status >= 500) return 'The server is having a moment. Please try again shortly.';
    return fallback;
  };

  const handleSimplify = async () => {
    setActionLoading('simplify');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/business-brain/business-builder/review/simplify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const { data } = await safeParse(response);

      if (response.ok && data?.success) {
        toast.success('Plan simplified! Review the updated version.');
        setPlan(data.plan);
        setReviewSections(data.review_sections);
        setIsApproved(false);
      } else {
        toast.error(backendError(data, response.status, 'Failed to simplify plan'));
      }
    } catch (error) {
      console.error('Error simplifying plan:', error);
      toast.error('Network hiccup while simplifying your plan. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRegenerate = async () => {
    setActionLoading('regenerate');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/business-brain/business-builder/review/regenerate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const { data } = await safeParse(response);

      if (response.ok && data?.success) {
        toast.success('Plan regenerated! Review the improved version.');
        setPlan(data.plan);
        setReviewSections(data.review_sections);
        setIsApproved(false);
      } else {
        toast.error(backendError(data, response.status, 'Failed to regenerate plan'));
      }
    } catch (error) {
      console.error('Error regenerating plan:', error);
      toast.error('Network hiccup while regenerating your plan. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  if (!isOpen) return null;

  const sectionIcons = {
    business_type: <Target className="w-5 h-5" />,
    business_idea: <Lightbulb className="w-5 h-5" />,
    audience: <Users className="w-5 h-5" />,
    monetization_path: <DollarSign className="w-5 h-5" />,
    first_offer: <Sparkles className="w-5 h-5" />,
    customer_acquisition_platform: <MapPin className="w-5 h-5" />,
    launch_path: <Rocket className="w-5 h-5" />,
    why_this_plan_fits: <Heart className="w-5 h-5" />
  };

  const sectionLabels = {
    business_type: 'Business Type',
    business_idea: 'Business Idea',
    audience: 'Target Audience',
    monetization_path: 'Monetization Path',
    first_offer: 'First Offer / Service',
    customer_acquisition_platform: 'Customer Platform',
    launch_path: 'Launch Path',
    why_this_plan_fits: 'Why This Fits You'
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden" data-testid="business-plan-review-modal">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Review Your Business Plan</h2>
                <p className="text-indigo-100 text-sm mt-1">Approve your plan before proceeding to Money Bot</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="close-review-modal"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mb-4"></div>
                <p className="text-slate-600">Loading your business plan...</p>
              </div>
            ) : (
              <>
                {/* Validation Warning */}
                {validation && !validation.is_valid && (
                  <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">This plan needs one more revision before you continue.</p>
                        <p className="text-sm text-amber-700 mt-1">
                          Missing: {validation.missing_fields?.join(', ')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Already Approved */}
                {isApproved && (
                  <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-medium text-emerald-800">Plan Approved!</p>
                        <p className="text-sm text-emerald-700">You can proceed to Money Bot.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Review Sections */}
                <div className="space-y-4">
                  {reviewSections && Object.entries(reviewSections).map(([key, value]) => (
                    <div
                      key={key}
                      className="bg-slate-50 border border-slate-200 rounded-xl p-4 hover:border-indigo-200 transition-colors"
                      data-testid={`review-section-${key}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600">
                          {sectionIcons[key] || <Target className="w-5 h-5" />}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900 text-sm uppercase tracking-wide">
                            {sectionLabels[key] || key.replace(/_/g, ' ')}
                          </h3>
                          <p className="text-slate-700 mt-1 whitespace-pre-line">{value || '—'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Actions Footer */}
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Approve Button */}
              <button
                onClick={handleApprove}
                disabled={actionLoading || isApproved}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/25"
                data-testid="approve-plan-button"
              >
                {actionLoading === 'approve' ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Approve Plan</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              {/* Simplify Button */}
              <button
                onClick={handleSimplify}
                disabled={actionLoading}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-amber-400 text-amber-700 hover:bg-amber-50 font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="simplify-plan-button"
              >
                {actionLoading === 'simplify' ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600"></div>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    <span>Simplify Plan</span>
                  </>
                )}
              </button>

              {/* Regenerate Button */}
              <button
                onClick={handleRegenerate}
                disabled={actionLoading}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-indigo-400 text-indigo-700 hover:bg-indigo-50 font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="regenerate-plan-button"
              >
                {actionLoading === 'regenerate' ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    <span>Regenerate Plan</span>
                  </>
                )}
              </button>
            </div>

            <p className="text-center text-xs text-slate-500 mt-3">
              You must approve your plan before proceeding to Money Bot
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessPlanReviewModal;
